import { Cause, Effect, Exit, Layer, Ref, Schema, Scope } from "effect";
import { workspaceInvalid } from "@workerstation/shared/errors/workspace-http";
import { AcceptedWork } from "../accepted-work.ts";
import { makeSessionOwner } from "../session-owner.ts";
import { PersistenceOrder } from "./persistence-order.ts";
import { StationPersistence } from "./persistence-service.ts";
import { WorkspaceDraft, WorkspaceTarget, workspacePersistenceKey } from "./persistence-model.ts";
import { PendingSaveSessions, type PendingSaveSession } from "./pending-service.ts";
import { pendingSaveSnapshot, type PendingEdit, type PendingSaveSnapshot, type PendingSaveState } from "./pending-model.ts";

const closed = (scope: Scope.Scope): boolean => scope.state._tag === "Closed";

export const PendingSaveSessionsLive = (root: Scope.Scope) => Layer.effect(PendingSaveSessions, Effect.gen(function* () {
  const persistence = yield* StationPersistence, accepted = yield* AcceptedWork, order = yield* PersistenceOrder;
  const sessions = yield* makeSessionOwner(root), sequence = yield* Ref.make(0);
  const notificationFailures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
  yield* Effect.addFinalizer(() => Ref.get(notificationFailures).pipe(Effect.flatMap(reasons =>
    reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void)));
  const make = (notify: (state: PendingSaveSnapshot) => Effect.Effect<void>) => Effect.gen(function* () {
    const owner = yield* Scope.Scope, id = yield* Ref.modify(sequence, value => [value + 1, value + 1] as const);
    const state = yield* Ref.make<PendingSaveState>({ pending: null, phase: "idle", timer: null, closed: false });
    const read = Ref.get(state).pipe(Effect.map(pendingSaveSnapshot));
    const publish = Effect.gen(function* () {
      if (closed(root) || closed(owner)) return;
      const snapshot = yield* read;
      if (snapshot.closed) return;
      const result = yield* Effect.exit(Effect.suspend(() => notify(snapshot)));
      if (Exit.isFailure(result)) yield* Ref.update(notificationFailures, current =>
        [...current, ...result.cause.reasons.filter(reason => reason._tag === "Die")]);
      return yield* result;
    });
    const update = (change: (current: PendingSaveState) => PendingSaveState) => Ref.update(state, change).pipe(Effect.andThen(publish));
    const stop = (scope: Scope.Closeable | null) => scope === null ? Effect.void : Scope.close(scope, Exit.void);
    yield* Effect.addFinalizer(() => Ref.getAndUpdate(state, current => ({ ...current, closed: true, timer: null })).pipe(
      Effect.flatMap(current => stop(current.timer))));
    const save = (pending: PendingEdit) => persistence.save(pending.target, pending.draft);
    const autosave = (pending: PendingEdit) => accepted.join(Effect.gen(function* () {
      const before = yield* Ref.get(state);
      if (before.closed || before.pending?.identity !== pending.identity || closed(root) || closed(owner)) return;
      const result = yield* Effect.exit(save(pending));
      yield* update(current => current.pending?.identity !== pending.identity ? current : {
        ...current, pending: Exit.isSuccess(result) ? null : pending,
        phase: Exit.isSuccess(result) ? "saved" : "error"
      });
    }));
    const session: PendingSaveSession = {
      read,
      schedule: Effect.fn("Frontend.PendingSaves.schedule")(function* (target, draft) {
        if (closed(root) || closed(owner) || (yield* Ref.get(state)).closed) return yield* Effect.interrupt;
        const prepared = yield* Schema.decodeUnknownEffect(WorkspaceTarget)(target).pipe(Effect.mapError(() => workspaceInvalid("Invalid pending-save target.")));
        const payload = yield* Schema.decodeUnknownEffect(WorkspaceDraft)(draft).pipe(Effect.mapError(() => workspaceInvalid("Invalid pending-save draft.")));
        const pending: PendingEdit = { identity: {}, target: prepared, draft: payload };
        yield* Effect.uninterruptible(Effect.gen(function* () {
          const timer = yield* Scope.fork(owner);
          const previous = yield* Ref.getAndUpdate(state, current => ({ ...current, pending, phase: "saving" as const, timer }));
          yield* stop(previous.timer);
          yield* publish;
          yield* Effect.forkIn(Effect.sleep(800).pipe(Effect.andThen(autosave(pending))), timer, { uninterruptible: false });
        }));
      }),
      flush: Effect.suspend(() => closed(root) || closed(owner) ? Effect.interrupt : accepted.join(order.run(JSON.stringify(["pending-flush", id]), Effect.gen(function* () {
        while (true) {
          const pending = yield* Ref.modify(state, current => [current.pending, { ...current, pending: null }] as const);
          if (pending === null) return;
          const result = yield* Effect.exit(save(pending));
          if (Exit.isFailure(result)) {
            yield* Ref.update(state, current => current.pending === null ? { ...current, pending } : current);
            return yield* result;
          }
        }
      })))),
      discard: Effect.fn("Frontend.PendingSaves.discard")(function* (target) {
        const previous = yield* Ref.modify(state, current => target !== undefined && (current.pending === null
          || workspacePersistenceKey(current.pending.target) !== workspacePersistenceKey(target))
          ? [null, current] as const : [current.timer, { ...current, pending: null, timer: null, phase: "idle" }] as const);
        yield* stop(previous); yield* publish;
      })
    };
    return session;
  });
  return PendingSaveSessions.of({ open: notify => sessions.open(make(notify)) });
}));
